import {
  IconTrashCan,
  IconPlus,
  GreyGrey50,
  GreyGrey70,
  Primary13Regular,
  SteppedNumberInput,
  PriceInput,
  SimpleTableColumn,
  SimpleTableInputProps,
  SimpleTableInvalidRow,
  SimpleTableRowActionsProps,
  formatWithCommas,
  Box,
  Flex
} from '@sequencehq/core-components'
import { FC, useCallback, useMemo } from 'react'
import { Currency } from '@sequencehq/api'
import {
  enforceMinimumPrecision,
  sanitizeTiers,
  ValidationError
} from '@sequencehq/utils'
import { PriceEditorTier } from 'common/drawers/PricingEditor/domain'

const tableInputStyle = {
  border: 'none',
  boxShadow: 'none',
  borderRadius: 'none'
}

const tableColumnsConfig: SimpleTableColumn<
  PriceEditorTier,
  keyof PriceEditorTier,
  {
    currency: Currency
  }
>[] = [
  {
    property: 'firstUnit',
    label: 'First seat',
    bodyComponent: ({ value, rowIndex }) => (
      <Box
        paddingLeft="12px"
        color={GreyGrey50}
        {...Primary13Regular}
        data-testid={`seat.tiers.${rowIndex}.firstUnit`}
      >
        {formatWithCommas(value ?? '')}
      </Box>
    )
  },
  {
    property: 'lastUnit',
    label: 'Last seat',
    bodyComponent: ({ value, onChange, onBlur, rowIndex, rows, disabled }) => {
      return (
        <SteppedNumberInput
          data-testid={`seat.tiers.${rowIndex}.lastUnit`}
          isDisabled={disabled || rowIndex === rows.length - 1}
          precision={0}
          onChange={newValue => {
            if (rowIndex === rows.length - 1) {
              return
            }
            return onChange(newValue)
          }}
          value={rowIndex === rows.length - 1 ? '∞' : (value ?? '')}
          {...tableInputStyle}
          paddingLeft="12px"
          onBlur={onBlur}
          placeholder="0"
          onFocus={() => {
            if (value === '0') {
              onChange('')
            }
          }}
        />
      )
    }
  },
  {
    property: 'unitPrice',
    label: 'Seat price',
    bodyComponent: ({
      value,
      onBlur,
      onChange,
      disabled,
      config,
      rowIndex
    }) => (
      <PriceInput
        data-testid={`seat.tiers.${rowIndex}.unitPrice`}
        isDisabled={disabled}
        currency={config.currency}
        onChange={onChange}
        onBlur={onBlur}
        value={value ?? ''}
        variant="table"
        placeholder="0.00"
        onFocus={() => {
          if (value === '0.00') {
            onChange('')
          }
        }}
        {...tableInputStyle}
      />
    )
  },
  {
    property: 'flatFee',
    label: 'Flat fee',
    bodyComponent: ({
      value,
      onBlur,
      onChange,
      disabled,
      config,
      rowIndex
    }) => (
      <PriceInput
        isDisabled={disabled}
        currency={config.currency}
        onChange={onChange}
        value={value ?? ''}
        onBlur={onBlur}
        variant="table"
        placeholder="0.00"
        onFocus={() => {
          if (value === '0.00') {
            onChange('')
          }
        }}
        data-testid={`seat.tiers.${rowIndex}.flatFee`}
        {...tableInputStyle}
      />
    )
  }
]

type UseSeatBasedTierInput = (props: {
  onChange: (value: PriceEditorTier[]) => void
  value: PriceEditorTier[]
  isDisabled: boolean
  currency: Currency
  validationErrors: ValidationError[]
}) => {
  fieldConfig: SimpleTableInputProps<
    PriceEditorTier,
    {
      currency: Currency
    }
  > & {
    invalidRows: SimpleTableInvalidRow<PriceEditorTier>[]
    onBlur: () => void
  }
  addNewTier: (rowIndex?: number) => void
  test: {
    onDelete: (id: PriceEditorTier['id']) => void
    rowActionsAvailable: {
      delete: boolean
      addNewTier: boolean
    }
  }
}

export const useSeatBasedTierInput: UseSeatBasedTierInput = props => {
  const rowActionsAvailable = useMemo(() => {
    return {
      delete: props.value && props.value.length > 1 && !props.isDisabled,
      addNewTier: !props.isDisabled
    }
  }, [props.value, props.isDisabled])

  const onDelete = useCallback(
    (id: PriceEditorTier['id']) => {
      const newTiers = props.value.filter(tier => tier.id !== id)

      props.onChange(sanitizeTiers(newTiers))
    },
    [props.value, props.onChange]
  )

  const addNewTier = useCallback(
    (rowIndex: number = props.value.length - 1) => {
      const previousTier = props.value[rowIndex]

      const newTiers = sanitizeTiers([
        ...props.value.slice(0, rowIndex + 1),
        {
          id: crypto.randomUUID(),
          /** These will be calculated and set by the prepare tiers functionality */
          firstUnit: '',
          lastUnit: '',
          unitPrice: enforceMinimumPrecision(2)(previousTier?.unitPrice),
          flatFee: enforceMinimumPrecision(2)(previousTier?.flatFee)
        },
        ...props.value.slice(rowIndex + 1)
      ])
      props.onChange(newTiers)
    },
    [props.value, props.onChange]
  )

  const rowActionsComponent: FC<SimpleTableRowActionsProps<PriceEditorTier>> =
    useMemo(() => {
      return ({ rowData, rowIndex }) => {
        return (
          <Flex alignItems="center" height="100%" padding="0 8px" gap="4px">
            <Box
              as="span"
              display="inline-flex"
              data-test="seat-based-delete-tier"
              color={rowActionsAvailable.delete ? GreyGrey70 : GreyGrey50}
              cursor={rowActionsAvailable.delete ? 'pointer' : undefined}
              _hover={{
                color: GreyGrey50
              }}
              onClick={() => rowActionsAvailable.delete && onDelete(rowData.id)}
            >
              <IconTrashCan color="inherit" />
            </Box>
            <Box
              as="span"
              display="inline-flex"
              data-test="seat-based-add-new-tier"
              color={rowActionsAvailable.addNewTier ? GreyGrey70 : GreyGrey50}
              cursor="pointer"
              _hover={{
                color: GreyGrey50
              }}
              onClick={() => {
                rowActionsAvailable.addNewTier && addNewTier(rowIndex)
              }}
            >
              <IconPlus color="inherit" />
            </Box>
          </Flex>
        )
      }
    }, [onDelete, addNewTier, rowActionsAvailable])

  const rowValidationErrors: SimpleTableInvalidRow<PriceEditorTier>[] =
    useMemo(() => {
      if (!props.validationErrors) {
        return []
      }

      return Object.values(
        props.validationErrors.reduce(
          (acc, error) => {
            const tierId = error.metadata?.tierId as string
            if (!tierId) {
              return acc
            }

            if (!acc[tierId]) {
              return {
                ...acc,
                [tierId]: {
                  id: tierId,
                  invalidProperties: [
                    error.metadata?.property as keyof PriceEditorTier
                  ]
                }
              }
            }

            return {
              ...acc,
              [tierId]: {
                ...acc[tierId],
                invalidProperties: [
                  ...(acc[tierId]?.invalidProperties ?? []),
                  error.metadata?.property as keyof PriceEditorTier
                ]
              }
            }
          },
          {} as Record<string, SimpleTableInvalidRow<PriceEditorTier>>
        )
      )
    }, [props.validationErrors])

  const onChange = useCallback(
    (newValue: PriceEditorTier[]) => {
      props.onChange(newValue)
    },
    [props.onChange]
  )

  const onBlur = useCallback(() => {
    props.onChange(sanitizeTiers(props.value))
  }, [props.onChange, props.value])

  return {
    addNewTier,
    fieldConfig: {
      columns: tableColumnsConfig,
      rowActions: {
        component: rowActionsComponent
      },
      invalidRows: rowValidationErrors,
      onChange,
      onBlur,
      rows: props.value,
      isDisabled: props.isDisabled,
      config: {
        currency: props.currency
      }
    },
    test: {
      onDelete,
      rowActionsAvailable
    }
  }
}
